import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Section from '../Section/Section';
import BreakdownDimension from '../BreakdownDimension/BreakdownDimension';
import DimensionPicker from '../DimensionPicker/DimensionPicker';
import { DIMENSIONS_OPTIONS, getAvailableOptions } from '../../consts';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS.FILTERS';
const MAX_DIMENSIONS = 3;

const DimensionsSection = ({ filtersRef, onChange }) => {
    const [dimensions, setDimensions] = useState(() => filtersRef.current.dimensions);

    useEffect(() => {
        filtersRef.current.dimensions = dimensions;
        onChange?.();
    }, [dimensions, filtersRef, onChange]);

    const isMaxDimensions = dimensions.length >= MAX_DIMENSIONS;

    const availableDimensionItems = useMemo(
        () =>
            getAvailableOptions(
                DIMENSIONS_OPTIONS,
                dimensions.map(d => d.value),
                null
            ).map(d => ({
                name: d.value,
                display_name: d.label,
            })),
        [dimensions]
    );

    const handleAddDimension = useCallback(selected => {
        const option = DIMENSIONS_OPTIONS.find(d => d.value === selected.name);
        if (!option) return;
        setDimensions(prev => {
            if (prev.length >= MAX_DIMENSIONS) return prev;
            return [...prev, option];
        });
    }, []);

    const handleRemoveDimension = useCallback(value => {
        setDimensions(prev => prev.filter(d => d.value !== value));
    }, []);

    const handleDragEnd = useCallback(({ active, over }) => {
        if (!over || active.id === over.id) return;
        setDimensions(prev => {
            const oldIndex = prev.findIndex(d => d.value === active.id);
            const newIndex = prev.findIndex(d => d.value === over.id);
            return arrayMove(prev, oldIndex, newIndex);
        });
    }, []);

    const sortableIds = useMemo(() => dimensions.map(d => d.value), [dimensions]);

    return (
        <Section icon="layers-intersect" labelKey={`${TRANSLATION_PREFIX}.BREAKDOWN_DIMENSIONS`}>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    {dimensions.map(dimension => (
                        <BreakdownDimension
                            key={dimension.value}
                            dimension={dimension}
                            onRemove={handleRemoveDimension}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            {!isMaxDimensions && (
                <DimensionPicker availableItems={availableDimensionItems} onSelect={handleAddDimension} />
            )}
        </Section>
    );
};

DimensionsSection.propTypes = {
    filtersRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    onChange: PropTypes.func,
};

export default DimensionsSection;
